import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { getPlanByProductId, type PlanKey, PLAN_TIER } from "@/lib/stripe-plans";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  subscribed: boolean;
  subscriptionPlan: PlanKey | null;
  subscriptionEnd: string | null;
  subscriptionLoading: boolean;
  refreshSubscription: () => Promise<void>;
  isTrial: boolean;
  trialPlan: string | null;
  canUseAI: boolean;
  effectivePlan: PlanKey | null;
  isPlatformAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
  subscribed: false,
  subscriptionPlan: null,
  subscriptionEnd: null,
  subscriptionLoading: true,
  refreshSubscription: async () => {},
  isTrial: false,
  trialPlan: null,
  canUseAI: false,
  effectivePlan: null,
  isPlatformAdmin: false,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<PlanKey | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [isTrial, setIsTrial] = useState(false);
  const [trialPlan, setTrialPlan] = useState<string | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  const checkSubscription = useCallback(async () => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession?.access_token) {
        setSubscribed(false);
        setSubscriptionPlan(null);
        setSubscriptionEnd(null);
        setIsTrial(false);
        setTrialPlan(null);
        setSubscriptionLoading(false);
        return;
      }
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) {
        console.error("Error checking subscription:", error);
        setSubscribed(false);
        setSubscriptionPlan(null);
        setSubscriptionEnd(null);
        setIsTrial(false);
        setTrialPlan(null);
        setIsPlatformAdmin(false);
      } else {
        setSubscribed(data.subscribed ?? false);
        setSubscriptionPlan(data.product_id ? getPlanByProductId(data.product_id) : null);
        setSubscriptionEnd(data.subscription_end ?? null);
        setIsTrial(data.is_trial ?? false);
        setTrialPlan(data.trial_plan ?? null);
        setIsPlatformAdmin(data.is_admin === true);
      }
    } catch (err) {
      console.error("Error checking subscription:", err);
      setSubscribed(false);
    } finally {
      setSubscriptionLoading(false);
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user) {
          setTimeout(() => checkSubscription(), 0);
        } else {
          setSubscribed(false);
          setSubscriptionPlan(null);
          setSubscriptionEnd(null);
          setIsTrial(false);
          setTrialPlan(null);
          setSubscriptionLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        checkSubscription();
      } else {
        setSubscriptionLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkSubscription]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(checkSubscription, 60000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{
      user, session, loading, signOut,
      subscribed, subscriptionPlan, subscriptionEnd, subscriptionLoading,
      refreshSubscription: checkSubscription,
      isTrial, trialPlan,
      canUseAI: subscribed && !isTrial,
      isPlatformAdmin,
      effectivePlan: (isPlatformAdmin
        ? "agency"
        : isTrial
          ? ((trialPlan as PlanKey) ?? "starter")
          : subscriptionPlan) as PlanKey | null,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
